"use client";

import { Button } from "@/components/ui/button";
import type { ColumnDef } from "@tanstack/react-table";
import { RefreshCw } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { DataTableColumnHeader } from "./components/data-table-header";
import { deleteDataById, syncPayload } from "./actions";
import React from "react";

// This type is used to define the shape of our data.
// You can use a Zod schema here if you want.
export type Payment = {
  ID: string;
  TransactionTime: string;
  TransactionStatus: string;
  TransactionID: string;
  StatusMessage: string;
  StatusCode: string;
  SignatureKey: string;
  PaymentType: string;
  OrderID: string;
  MerchantID: string;
  GrossAmount: string;
  FraudStatus: string;
  Currency: string;
  CustomerName: string;
  CustomerEmail: string;
  CustomerPhone: string;
  Mode: string;
  CreatedAt: string;
  UpdatedAt: string;
  DeletedAt: string | null;
};

export const columns: ColumnDef<Payment>[] = [
  {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && "indeterminate")
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: "Mode",
    header: ({ column }) => {
      return <DataTableColumnHeader column={column} title="Mode" />;
    },
    cell: ({ row }) => {
      const mode: string = row.getValue("Mode");
      return (
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
            mode === "sandbox"
              ? "bg-yellow-100 text-yellow-800"
              : "bg-green-100 text-green-800"
          }`}
        >
          {mode === "sandbox" ? "Sandbox" : "Production"}
        </span>
      );
    },
  },
  {
    accessorKey: "CustomerName",
    header: ({ column }) => {
      return <DataTableColumnHeader column={column} title="Nama Pelanggan" />;
    },
  },
  {
    accessorKey: "CustomerEmail",
    header: ({ column }) => {
      return <DataTableColumnHeader column={column} title="Email Pelanggan" />;
    },
  },
  {
    accessorKey: "CustomerPhone",
    header: ({ column }) => {
      return <DataTableColumnHeader column={column} title="Nomor Pelanggan" />;
    },
  },
  {
    accessorKey: "TransactionTime",
    header: ({ column }) => {
      return <DataTableColumnHeader column={column} title="Waktu Pembayaran" />;
    },
    cell: ({ row }) => {
      const value: string = row.getValue("TransactionTime");
      return new Intl.DateTimeFormat("id-ID", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(value));
    },
  },
  {
    accessorKey: "TransactionStatus",
    header: ({ column }) => {
      return (
        <DataTableColumnHeader column={column} title="Status Pembayaran" />
      );
    },
  },
  {
    accessorKey: "TransactionID",
    header: ({ column }) => {
      return <DataTableColumnHeader column={column} title="ID Pembayaran" />;
    },
  },

  {
    accessorKey: "StatusMessage",
    header: ({ column }) => {
      return <DataTableColumnHeader column={column} title="Pesan Status" />;
    },
  },
  {
    accessorKey: "StatusCode",
    header: ({ column }) => {
      return <DataTableColumnHeader column={column} title="Kode Status" />;
    },
  },
  {
    accessorKey: "SignatureKey",
    header: ({ column }) => {
      return <DataTableColumnHeader column={column} title="Signature Key" />;
    },
  },
  {
    accessorKey: "PaymentType",
    header: ({ column }) => {
      return <DataTableColumnHeader column={column} title="Jenis Pembayaran" />;
    },
  },
  {
    accessorKey: "OrderID",
    header: ({ column }) => {
      return <DataTableColumnHeader column={column} title="ID Pesanan" />;
    },
  },
  {
    accessorKey: "MerchantID",
    header: ({ column }) => {
      return <DataTableColumnHeader column={column} title="ID Merchant" />;
    },
  },
  {
    accessorKey: "GrossAmount",
    header: ({ column }) => {
      return <DataTableColumnHeader column={column} title="Harga" />;
    },
    cell: ({ row }) => {
      const value = row.getValue("GrossAmount");
      return new Intl.NumberFormat("id-ID", {
        style: "currency",
        currency: "IDR",
      }).format(Number(value));
    },
  },
  {
    accessorKey: "FraudStatus",
    header: ({ column }) => {
      return <DataTableColumnHeader column={column} title="Status Fraud" />;
    },
  },
  {
    accessorKey: "Currency",
    header: ({ column }) => {
      return <DataTableColumnHeader column={column} title="Mata Uang" />;
    },
  },

  {
    accessorKey: "CreatedAt",
    header: ({ column }) => {
      return (
        <DataTableColumnHeader column={column} title="Tanggal Pembuatan" />
      );
    },
    cell: ({ row }) => {
      const value: string = row.getValue("CreatedAt");
      return new Intl.DateTimeFormat("id-ID", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(value));
    },
  },
  {
    accessorKey: "UpdatedAt",
    header: ({ column }) => {
      return (
        <DataTableColumnHeader column={column} title="Tanggal Pembaruan" />
      );
    },
    cell: ({ row }) => {
      const value: string = row.getValue("UpdatedAt");
      return new Intl.DateTimeFormat("id-ID", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(value));
    },
  },
  {
    accessorKey: "DeletedAt",
    header: ({ column }) => {
      return (
        <DataTableColumnHeader column={column} title="Tanggal Penghapusan" />
      );
    },
    cell: ({ row }) => {
      const value: string = row.getValue("DeletedAt");
      return new Intl.DateTimeFormat("id-ID", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(value));
    },
  },
  {
    id: "actions",
    header: "Aksi",
    enableHiding: false,
    cell: ({ row }) => {
      const orderID: string = row.original.OrderID;
      const SyncButton = () => {
        const [syncing, setSyncing] = React.useState(false);
        const [status, setStatus] = React.useState<"idle" | "ok" | "err">("idle");

        const handleSync = async () => {
          setSyncing(true);
          setStatus("idle");
          const result = await syncPayload(orderID);
          setSyncing(false);
          setStatus(result.success ? "ok" : "err");
          if (!result.success) {
            alert(`Sync gagal: ${result.error}`);
          }
          setTimeout(() => setStatus("idle"), 3000);
        };

        return (
          <Button
            size="sm"
            variant={status === "ok" ? "outline" : status === "err" ? "destructive" : "secondary"}
            onClick={handleSync}
            disabled={syncing}
            className="gap-1.5 text-xs"
            title={`Re-forward payload ${orderID} ke semua destination aktif`}
          >
            <RefreshCw className={`w-3 h-3 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Syncing..." : status === "ok" ? "Synced ✓" : "Sync"}
          </Button>
        );
      };
      return <SyncButton />;
    },
  },
];
